import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import { PaymentService } from '../../services/payment/payment.service';
import { UserService } from '../../services/user/user.service';
import { StripeService } from '../../services/stripe/stripe.service';

@Component({
    selector: 'app-stripe-embedded-checkout',
    templateUrl: './stripe-embedded-checkout.component.html',
    styleUrls: ['./stripe-embedded-checkout.component.scss']
})
export class StripeEmbeddedCheckoutComponent implements OnChanges, AfterViewInit {

    loading = false;
    clientSecret?: string;
    error?: string;

    @Input() planId?: string;
    @Input() billingPeriod: 'monthly' | 'yearly' = 'monthly';
    /** When true, fetches client secret from update-payment-method API (no planId required). */
    @Input() updatePaymentMode = false;
    /** When true, uses old-company checkout API (dashboard subscription onboarding). */
    @Input() useOldCompanyCheckout = false;
    /** When true, asks the backend to replace the company's current Stripe subscription. */
    @Input() replaceExisting = false;
    /** When false, "Company already has a service" is shown as an error instead of treated as success. */
    @Input() treatExistingServiceAsSuccess = true;
    @Input() companyId?: string;
    @Input() email?: string;
    @Input() compact = false;

    @Output() paymentCompleted = new EventEmitter<void>();

    @ViewChild('checkoutContainer', { static: false }) checkoutContainer?: ElementRef<HTMLDivElement>;

    private stripeInstance: any;
    private embeddedCheckout: any;

    constructor(
        private paymentService: PaymentService,
        private userService: UserService,
        private stripeService: StripeService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (this.updatePaymentMode) {
            if ((changes['updatePaymentMode'] || changes['companyId']) && !this.clientSecret && !this.loading) {
                this.fetchClientSecret();
            }
        } else if ((changes['planId'] || changes['billingPeriod'] || changes['email'] || changes['companyId'] || changes['replaceExisting']) && this.planId && !this.clientSecret && !this.loading) {
            this.fetchClientSecret();
        }
    }

    ngAfterViewInit() {
        if (this.updatePaymentMode && !this.clientSecret && !this.loading) {
            this.fetchClientSecret();
        } else if (this.clientSecret && !this.embeddedCheckout) {
            this.initEmbeddedCheckout();
        }
    }

    async fetchClientSecret() {
        this.loading = true;
        this.error = undefined;

        try {
            let response: any;

            if (this.updatePaymentMode) {
                console.log('StripeEmbeddedCheckoutComponent: fetchClientSecret (update payment)', this.companyId);
                response = await this.paymentService.getUpdatePaymentMethodClientSecret(this.companyId);
            } else if (this.useOldCompanyCheckout) {
                console.log('StripeEmbeddedCheckoutComponent: fetchClientSecret (old company checkout)', this.companyId, this.billingPeriod);
                if (!this.companyId) {
                    this.error = 'ID società mancante per il checkout';
                    return;
                }
                response = await this.paymentService.getOldCompanyCheckoutClientSecret(
                    this.companyId,
                    this.billingPeriod,
                    this.planId ?? '',
                    this.email,
                    {replaceExisting: this.replaceExisting},
                );
            } else {
                console.log('StripeEmbeddedCheckoutComponent: fetchClientSecret', this.planId, this.billingPeriod);
                if (!this.planId) {
                    this.error = 'Nessun piano selezionato';
                    return;
                }
                response = await this.paymentService.getServiceCheckoutClientSecret(
                    this.planId,
                    this.billingPeriod,
                    this.email
                );
            }

            if (response && response.success) {
                this.clientSecret = response.data?.client_secret || response.data?.clientSecret || '';
                setTimeout(() => this.initEmbeddedCheckout(), 0);
            } else if (response) {
                const errorMessage: string = response.error || response.message || '';

                // Special case: company already has an active service.
                // This happens if the user refreshes while still on the payment page.
                if (this.useOldCompanyCheckout && this.treatExistingServiceAsSuccess && errorMessage === 'Company already has a service') {
                    // Refresh JWT so the new subscription is reflected in the token,
                    // then treat this as a completed payment and send user back to dashboard.
                    await this.userService.refreshJwtFromRefreshToken(true);
                    this.paymentCompleted.emit();
                    return;
                }

                this.error = response.message || 'Errore nel recupero del client_secret di Stripe';
            } else {
                this.error = 'Nessuna risposta dal server';
            }
        } catch (e) {
            this.error = 'Errore imprevisto nel recupero del client_secret di Stripe';
        } finally {
            this.loading = false;
        }
    }

    private async initEmbeddedCheckout() {
        const container = this.checkoutContainer?.nativeElement;
        if (!this.clientSecret || !container) {
            console.error('StripeEmbeddedCheckoutComponent: clientSecret o checkoutContainer non trovati', this.clientSecret, container);
            return;
        }

        try {
            if (!this.stripeInstance) {
                this.stripeInstance = await this.stripeService.getInstance();
            }

            if (!this.stripeInstance || !this.stripeInstance.initEmbeddedCheckout) {
                console.error('StripeEmbeddedCheckoutComponent: stripeInstance o initEmbeddedCheckout non trovati');
                this.error = 'Stripe non è configurato correttamente nel frontend.';
                return;
            }

            if (this.embeddedCheckout && this.embeddedCheckout.destroy) {
                console.error('StripeEmbeddedCheckoutComponent: embeddedCheckout o destroy non trovati');
                this.embeddedCheckout.destroy();
            }

            this.embeddedCheckout = await this.stripeInstance.initEmbeddedCheckout({
                clientSecret: this.clientSecret,
                onComplete: () => {
                    void this.onStripeCheckoutComplete();
                }
            });

            await this.embeddedCheckout.mount(container);
        } catch (e) {
            console.error('Errore nell\'inizializzazione di Stripe embedded checkout', e);
            this.error = 'Errore durante il rendering del checkout Stripe.';
        }
    }

    private checkoutSessionId(): string | undefined {
        const secret = this.clientSecret ?? '';
        const marker = '_secret_';
        const index = secret.indexOf(marker);
        if (index <= 0) {
            return undefined;
        }
        return secret.slice(0, index);
    }

    private async onStripeCheckoutComplete(): Promise<void> {
        const sessionId = this.checkoutSessionId();
        if (sessionId) {
            try {
                await this.paymentService.completeCheckoutSession(sessionId, this.companyId);
            } catch (error) {
                console.error('Unable to persist Stripe payment method after checkout', error);
            }
        }
        this.paymentCompleted.emit();
    }
}

